export const escapeHtml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const HEADING_LEVELS = [1, 2, 3, 4, 5, 6];

const renderChildren = (node) => (node.children || []).map(renderNode).join("");

const renderNode = (node) => {
  if (!node || typeof node !== "object") return "";

  switch (node.type) {
    case "root":
      return renderChildren(node);

    case "paragraph":
      return `<p>${renderChildren(node)}</p>`;

    case "heading": {
      const level = HEADING_LEVELS.includes(node.level) ? node.level : 2;

      return `<h${level}>${renderChildren(node)}</h${level}>`;
    }

    case "list": {
      const tag = node.listType === "ordered" ? "ol" : "ul";

      return `<${tag}>${renderChildren(node)}</${tag}>`;
    }

    case "list-item":
      return `<li>${renderChildren(node)}</li>`;

    case "link": {
      const href = escapeHtml(node.url || "");
      const title = node.title ? ` title="${escapeHtml(node.title)}"` : "";
      const target = node.target ? ` target="${escapeHtml(node.target)}"` : "";
      const rel = node.target === "_blank" ? ' rel="noopener"' : "";

      return `<a href="${href}"${title}${target}${rel}>${renderChildren(node)}</a>`;
    }

    case "text": {
      let html = escapeHtml(node.value || "");

      if (node.italic) html = `<em>${html}</em>`;
      if (node.bold) html = `<strong>${html}</strong>`;

      return html;
    }

    default:
      // Unknown node types still have renderable children often enough to be
      // worth descending into rather than dropping.
      return renderChildren(node);
  }
};

/**
 * Renders Shopify's rich text document JSON as HTML.
 *
 * The Storefront API hands back a `rich_text_field`'s raw JSON document, where
 * Liquid's `| metafield_tag` would have rendered it server-side.
 *
 * @param {string|Object} document - the metafield's `value`, or a parsed document
 * @returns {string} HTML, with every text node escaped
 */
export default function richTextToHtml(document) {
  if (!document) return "";

  let parsed = document;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      // Not a document after all — a plain string is still worth showing.
      return `<p>${escapeHtml(document)}</p>`;
    }
  }

  return renderNode(parsed);
}
